import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { window } from 'ngx-bootstrap/utils';
import { ToastrService } from 'ngx-toastr';
import { filter, firstValueFrom, Observable, SubscriptionLike } from 'rxjs';

import { AppService } from '../../../../../../app.service';
import {
  Dietitian,
  DietitianProps,
} from '../../../../../../data/dietitian/domain/dietitian';
import { DietitianState } from '../../../../../../data/dietitian/domain/dietitian_state_provider';
import { Subscription } from '../../../../../../data/dietitian/domain/subscription/subscription';
import { SegmentedNavigationItemModel } from '../../../../components/segmented-navigation/components/segmented-navigation-item/segmented-navigation-item.model';
import { AccountMsdpLinkFacade } from './account-msdp-link.facade';

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'msd-account-msdp-link',
  templateUrl: './account-msdp-link.component.html',
  styleUrls: ['./account-msdp-link.component.scss'],
  providers: [AccountMsdpLinkFacade],
})
export class AccountMsdpLinkComponent implements OnInit, OnDestroy {
  latestSubscription: Subscription | undefined;
  isLoading = true;
  loadingSubscription = true;
  firstname: string | undefined;
  lastname: string | undefined;
  phoneNumber: string | undefined;
  adeliNumber: string | undefined;
  email: string | undefined;
  password: string | undefined;
  rePassword: string | undefined;
  initUser = true;
  initPassword = true;
  initRePassword = true;
  errorSubscription?: string;
  segmentedItems: SegmentedNavigationItemModel[] = [
    new SegmentedNavigationItemModel(
      'signup',
      'Je crée mon compte',
      undefined,
      true,
    ),
    new SegmentedNavigationItemModel(
      'login',
      "J'ai déjà un compte",
      undefined,
      false,
    ),
  ];
  dietitian: Dietitian | undefined;
  dietitianState$: Observable<DietitianState>;
  submitted = false;

  private dietitianSubscription: SubscriptionLike;

  constructor(
    private facade: AccountMsdpLinkFacade,
    private appService: AppService,
    private router: Router,
    private toastr: ToastrService,
  ) {
    this.dietitianState$ = this.facade.dietitianState$;
    this.dietitianSubscription = this.dietitianState$.subscribe(
      (state) => (this.dietitian = state.entity),
    );
  }

  ngOnInit(): void {
    firstValueFrom(
      this.dietitianState$.pipe(filter((state) => state.entity != null)),
    ).then((state) => {
      this.dietitian = state.entity;
      this.firstname = this.dietitian?.firstName;
      this.lastname = this.dietitian?.lastName;
      this.phoneNumber = this.dietitian?.phoneNumber;
      this.email = this.dietitian?.email;
      this.adeliNumber = this.dietitian?.adeliNumber;
      this.initUser = this.dietitian?.email ?? false ? false : true;
      if (this.dietitian) {
        this.loadSubscription(this.dietitian);
      }
    });
  }

  loadSubscription(dietitian: Dietitian | undefined) {
    if (dietitian) {
      this.loadingSubscription = true;
      this.facade
        .geLatestSubscription(dietitian)
        .then((res) => {
          this.latestSubscription = res ? res.subscription : undefined;
        })
        .catch((err) => {
          this.errorSubscription = err;
        })
        .finally(() => {
          this.loadingSubscription = false;
        });
    } else {
      this.appService.logger.error('❌ Dietitian is null.');
    }
  }

  userInvalid(user: string | undefined) {
    if (this.initUser) {
      return false;
    }
    if (user) {
      const pattern = /^([a-zA-Z0-9._%-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})$/;
      return !pattern.test(user);
    }
    return true;
  }

  passwordInvalid(password: string | undefined) {
    if (this.initPassword) {
      return false;
    }
    return !password;
  }

  rePasswordInvalid(
    password: string | undefined,
    rePassword: string | undefined,
  ) {
    if (this.initRePassword) {
      return false;
    }
    return !rePassword || password !== rePassword;
  }

  formInvalid() {
    return (
      (this.segmentedItems[1].isActive &&
        (this.initUser ||
          this.initPassword ||
          this.userInvalid(this.email) ||
          this.passwordInvalid(this.password))) ||
      (this.segmentedItems[0].isActive &&
        (this.initUser ||
          this.initPassword ||
          this.initRePassword ||
          this.firstNameInvalid(this.firstname) ||
          this.lastNameInvalid(this.lastname) ||
          this.phoneNumberInvalid(this.phoneNumber) ||
          this.adeliNumberInvalid(this.adeliNumber) ||
          this.userInvalid(this.email) ||
          this.passwordInvalid(this.password) ||
          this.rePasswordInvalid(this.password, this.rePassword)))
    );
  }

  onSegmentChange(changed: SegmentedNavigationItemModel) {
    this.segmentedItems.forEach(
      (item) => (item.isActive = changed === item ? true : false),
    );
  }

  firstNameInvalid(firstname: string | undefined) {
    return !firstname;
  }

  lastNameInvalid(lastname: string | undefined) {
    return !lastname;
  }

  phoneNumberInvalid(phoneNumber: string | undefined) {
    return !phoneNumber;
  }

  adeliNumberInvalid(adeliNumber: string | undefined) {
    return !adeliNumber || adeliNumber.length < 9 || adeliNumber.length > 11;
  }

  isLinked() {
    return this.facade.isLinked(this.dietitian);
  }

  async signup(
    firstName: string | undefined,
    lastName: string | undefined,
    phoneNumber: string | undefined,
    adeli: string | undefined,
    user: string | undefined,
    password: string | undefined,
  ) {
    if (!!firstName && !!lastName && !!phoneNumber && !!user && !!password) {
      if (!this.submitted) {
        this.submitted = true;
        try {
          await this.facade.updateAccount(<DietitianProps>{
            firstName,
            lastName,
            phoneNumber,
            adeliNumber: adeli,
            email: user,
          });
          await this.facade.signup(this.dietitian, user, password);
          this.submitted = false;
        } catch (e: any) {
          this.toastr.error(e.message);
          this.submitted = false;
        }
      }
    }
  }

  async login(user: string | undefined, password: string | undefined) {
    if (!!user && !!password) {
      if (!this.submitted) {
        try {
          this.submitted = true;
          await this.facade.login(
            this.dietitian,
            user.toLowerCase().replaceAll(/\\s/g, ''),
            password,
          );
          this.submitted = false;
        } catch (e: any) {
          this.toastr.error(e.message);
          this.submitted = false;
        }
      }
    }
  }

  async link() {
    if (this.dietitian && !this.submitted) {
      this.submitted = true;
      try {
        return await this.facade.link(this.dietitian);
      } catch (e) {
        return Promise.reject(e);
      } finally {
        this.submitted = false;
      }
    } else {
      return Promise.reject();
    }
  }

  // async unlink() {
  //   if (this.dietitian) {
  //     await this.facade.unlink(this.dietitian);
  //     this.onSegmentChange(this.segmentedItems[0]);
  //   }
  // }

  public get isAlreadySubscribed(): boolean {
    return !!this.latestSubscription && this.latestSubscription.isActive;
  }

  public get isTrialing(): boolean {
    return !!this.latestSubscription && this.latestSubscription.isTrialing;
  }

  goSubscription() {
    this.router
      .navigate(['dashboard', 'account', 'subscription'], { replaceUrl: true })
      .then();
  }

  async openMsdp() {
    try {
      const url = await this.link();
      window.open(url, '_blank');
    } catch (e: any) {
      this.appService.logger.error('openMsdp', e);
      if (e instanceof HttpErrorResponse) {
        this.toastr.error(e.error.message);
        if (e.error.reason === 'INVALID_LOGIN_CREDENTIALS') {
          if (this.dietitian) {
            await this.facade.unlink(this.dietitian);
            this.segmentedItems[0].isActive = false;
            this.segmentedItems[1].isActive = true;
            this.toastr.info(
              'Vous avez peut-être modifié votre mot de passe MSD+ ! Veuillez vous connecter à MSD+.',
            );
          }
        }
      }
    }
  }

  ngOnDestroy(): void {
    this.dietitianSubscription?.unsubscribe();
  }
}
